<script setup lang="ts">
import { ElMessage } from "element-plus";
import { computed, onMounted, ref } from "vue";
import { RemoveRolesAndGroupsDto, SysOrgRoleName } from "~/api";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";
import { splitByCaps } from "~/utils/miscUtils";

const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits<{
  (e: "update:visible", value: boolean): void;
  (e: "applied"): void;
}>();

const myInfoStore = useMyInfoStore();

const selectedRoles = ref<SysOrgRoleName[]>([]);
const selectedGroupGuids = ref<string[]>([]);
const isLoading = ref(false);

// Get current user's roles and groups
const currentRoles = computed(() => {
  return myInfoStore.currentSysOrg?.roles ?? [];
});

const currentGroups = computed(() => {
  const groupGuids = myInfoStore.currentSysOrg?.groupGuids ?? [];
  // Get group names from orgInfoStore if available, otherwise just show GUIDs
  return groupGuids.map((guid) => ({
    guid,
    name: `Group ${guid.substring(0, 8)}...`, // Fallback to showing partial GUID
  }));
});

const roleOptions = computed(() => {
  return currentRoles.value.map((role) => ({
    value: role,
    label: splitByCaps(role),
  }));
});

const groupOptions = computed(() => {
  return currentGroups.value.map((group) => ({
    value: group.guid,
    label: group.name,
  }));
});

const canApply = computed(() => {
  return (
    selectedRoles.value.length > 0 ||
    selectedGroupGuids.value.length > 0 ||
    removeSuperAdmin.value
  );
});

const handleClose = () => {
  // Reset selections
  selectedRoles.value = [];
  selectedGroupGuids.value = [];
  emit("update:visible", false);
};

const removeSuperAdmin = ref(false);

const handleApply = async () => {
  if (!canApply.value) {
    ElMessage.warning("Please select at least one role or group to remove");
    return;
  }

  isLoading.value = true;

  try {
    const request: RemoveRolesAndGroupsDto = {
      sysOrgGuid: myInfoStore.currentSysOrg?.guid,
      rolesToRemove:
        selectedRoles.value.length > 0 ? selectedRoles.value : null,
      groupGuidsToRemove:
        selectedGroupGuids.value.length > 0 ? selectedGroupGuids.value : null,
      removeSuperAdmin: myInfoStore.isSuperAdmin && removeSuperAdmin.value,
    };

    await apiClient.userInfo.removeRolesAndGroups(request);

    ElMessage.success(
      "Roles and groups removed successfully. Reloading page...",
    );

    // Close dialog
    emit("update:visible", false);

    // Emit applied event so parent can reload page
    emit("applied");
  } catch (error) {
    showError("Failed to remove roles and groups", error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  await myInfoStore.load();
});
</script>

<template>
  <el-dialog
    :model-value="visible"
    title="Remove Roles or Groups"
    width="500px"
    @update:model-value="emit('update:visible', $event)"
    @close="handleClose"
  >
    <div class="dialog-content">
      <p class="description">
        Select roles and groups to remove for testing purposes. Use "Refresh
        Settings" to restore them.
      </p>

      <div v-if="roleOptions.length > 0" class="selection-section">
        <h4>Roles to Remove:</h4>
        <el-checkbox-group v-model="selectedRoles">
          <el-checkbox
            v-for="role in roleOptions"
            :key="role.value"
            :value="role.value"
            :label="role.value"
          >
            {{ role.label }}
          </el-checkbox>
        </el-checkbox-group>
      </div>

      <div v-if="groupOptions.length > 0" class="selection-section">
        <h4>Groups to Remove:</h4>
        <el-checkbox-group v-model="selectedGroupGuids">
          <el-checkbox
            v-for="group in groupOptions"
            :key="group.value"
            :value="group.value"
            :label="group.value"
          >
            {{ group.label }}
          </el-checkbox>
        </el-checkbox-group>
      </div>

      <div v-if="myInfoStore.isSuperAdmin" class="selection-section">
        <h4>Super Admin:</h4>
        <el-checkbox v-model="removeSuperAdmin" :label="'Remove Super Admin'" />
      </div>

      <div
        v-if="roleOptions.length === 0 && groupOptions.length === 0"
        class="no-items"
      >
        <p>No roles or groups available to remove.</p>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <el-button :disabled="isLoading" @click="handleClose">
          Cancel
        </el-button>
        <el-button
          type="primary"
          :disabled="!canApply || isLoading"
          :loading="isLoading"
          @click="handleApply"
        >
          Apply
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped>
.dialog-content {
  padding: 10px 0;
}

.description {
  margin-bottom: 20px;
  color: var(--el-text-color-regular);
  font-size: 14px;
}

.selection-section {
  margin-bottom: 20px;
}

.selection-section h4 {
  margin-bottom: 10px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.el-checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.no-items {
  text-align: center;
  color: var(--el-text-color-regular);
  padding: 20px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
